import { css } from '@emotion/react';
import React from 'react';

import { CategoryAddressProvider } from '~src/address/search-address/address-provider';
import { CategoryExtensionProps } from '~src/category/category-manager';
import { useLogPosthogEvent } from '~src/commons/services/posthog-service';
import { useTranslations } from '~src/commons/utils/translations/translate';

import { useHasAddress } from '~address-form/utils/address-utils';

import { CustomerNumberExtension } from '../additional-info/customer-number-extension';
import { CustomerNumberProvider } from '../additional-info/customer-number-provider';
import { DoorCodeExtension } from '../additional-info/door-code-extension';
import { DeliveryTimeExtension } from '../delivery-time/delivery-time-extension';
import { TimeSlotExtension } from '../delivery-time/time-slot-extension';
import { CustomTextExtension } from '../extension/custom-text-extension';
import { DeliveryAddonExtension } from '../extension/delivery-addon-extension';
import { LabelExtension } from '../extension/label-extension';
import { LogoExtension } from '../extension/logo-extension';
import { useGlobalState } from '../global-state';
import { HomeDeliveryExtension } from '../location/home-delivery-extension';
import { LocationExtension } from '../location/location-extension';
import { PriceExtension } from '../price/price-extension';
import { mediaQueries } from '../theme/mediaQueries';
import { useAriaGridCell } from '../ui/aria-grid';
import { ShippingCardUI } from '../ui/shipping-card';
import { useCategoryState } from './category-state';

export const CategoryCard: React.FC<CategoryExtensionProps> = props => {
  const { listState, selected, onSelectCategory } = props;
  const category = useCategoryState();
  const { tICU } = useTranslations();

  const ref = React.useRef(null);

  const callSelectCategory = () => {
    if (selected) {
      // Concurrent update risk if we fire here.
      return;
    }

    onSelectCategory();
  };

  const { cellProps } = useAriaGridCell({
    itemKey: category.id,
    listState,
    ref,
    onSelect: callSelectCategory,
  });

  const card = (
    <ShippingCardUI
      headerExtensions={{
        logo: <LogoExtension {...props} />,
        price: <PriceExtension {...props} />,
        subtitle: <DeliveryTimeExtension {...props} />,
      }}
      selected={selected}
      title={category.name}
      radioTitle={
        selected
          ? tICU(`widget.aria.SELECTED_DELIVERY_OPTION`)
          : tICU(`widget.aria.DELIVERY_OPTION`)
      }
      reserveLogoSpace
      spacing="normal"
      innerRef={ref}
      {...cellProps}
    >
      <div css={styles.hackyHeaderStyles}>
        <LabelExtension {...props} />
      </div>
      <HomeDeliveryExtension {...props} />
      <LocationExtension {...props} />
      <TimeSlotExtension {...props} />
      <DoorCodeExtension {...props} />
      <CustomerNumberExtension {...props} />
      <DeliveryAddonExtension {...props} />
      <CustomTextExtension {...props} />
    </ShippingCardUI>
  );

  /*
   * We do not need the "row" but aria does.
   */
  return <div role="row">{card}</div>;
};

/**
 * Encapsulate processing that need to be done before we can really select a
 * category.
 *
 * If customer number is required then we have to ask for it before we select
 * the category. This does not fit nicely to the whole extension model, so
 * make an exception here until we find a better solution.
 */
export const SelectableCategoryCard: React.FC<CategoryExtensionProps> = ({
  onSelectCategory,
  ...props
}) => {
  const globalState = useGlobalState();
  const category = useCategoryState();
  const hasAddress = useHasAddress();

  const logPosthogEvent = useLogPosthogEvent();

  const requireAddress = category.flags.require_address && !hasAddress;
  const requireCustomerNumber =
    props.selectedOption.flags.show_customer_number &&
    !globalState.additional_info?.customer_number;

  /**
   * This checks do not fit nicely into the extension model because we have
   * to do some processing and display modal before we even select a
   * category. Maybe think of doing it nicier, but maybe it is ok like this.
   */
  return (
    <CategoryAddressProvider>
      {categoryChildProps => {
        const callSelectCategory1 = requireAddress
          ? () => {
              categoryChildProps.onOpen();
              logPosthogEvent('Address Required For Category Modal Viewed');
            }
          : onSelectCategory;
        /**
         * Note: Providing customer number is mandatory, user cannot select a category
         * without it because it could hinder later checkout process.
         */
        return (
          <CustomerNumberProvider onSuccess={callSelectCategory1}>
            {customerNumberChildProps => {
              const callSelectCategory2 = requireCustomerNumber
                ? customerNumberChildProps.onOpen
                : callSelectCategory1;
              return <CategoryCard {...props} onSelectCategory={callSelectCategory2} />;
            }}
          </CustomerNumberProvider>
        );
      }}
    </CategoryAddressProvider>
  );
};

const styles = {
  /*
   * Hack: introduce this style to capture all on-demand styles in
   * category-level selectable card.
   */
  hackyHeaderStyles: css({
    display: 'grid',
    /*
     * There is only 9px between delivery time and labels, not 16px as usual.
     */
    '& > *': {
      marginTop: '9px',
    },
    [mediaQueries.M]: {
      /*
       * Delivery time and label should have indent event on mobile.
       */
      marginLeft: '30px',
    },
  }),
};
